import { useEffect } from 'react';
import { useUI } from '../contexts';

export function useKeyboardShortcuts() {
  const {
    goToFileOpen,
    commandPaletteOpen,
    searchPanelOpen,
    globalSearchOpen,
    goToSymbolOpen,
    setGoToFileOpen,
    setGoToQuery,
    setGoToHighlight,
    setCommandPaletteOpen,
    setCommandQuery,
    setCommandHighlight,
    setSearchPanelOpen,
    setGlobalSearchOpen,
    setGlobalSearchMode,
    setGlobalSearchQuery,
    setGlobalSearchResults,
    setGoToSymbolOpen,
    setSymbolQuery,
    setSymbolHighlight,
    zenMode,
    setZenMode,
    goToInputRef,
    commandInputRef,
    searchInputRef,
    globalSearchInputRef,
    symbolInputRef,
  } = useUI();
  // Global keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isMod = event.metaKey || event.ctrlKey;
      // Cmd+P - Go to file
      if (isMod && event.key.toLowerCase() === 'p' && !event.shiftKey) {
        event.preventDefault();
        setGoToFileOpen(true);
        setGoToQuery('');
        setGoToHighlight(0);
        setCommandPaletteOpen(false);
        setSearchPanelOpen(false);
      }
      // Cmd+Shift+P - Command palette
      else if (isMod && event.shiftKey && event.key.toLowerCase() === 'p') {
        event.preventDefault();
        setCommandPaletteOpen(true);
        setCommandQuery('');
        setCommandHighlight(0);
        setGoToFileOpen(false);
        setSearchPanelOpen(false);
      }
      // Cmd+Shift+F - Global search in all files
      else if (isMod && event.shiftKey && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        setGlobalSearchOpen(true);
        setGlobalSearchMode('search');
        setGlobalSearchQuery('');
        setGlobalSearchResults([]);
        setGoToFileOpen(false);
        setCommandPaletteOpen(false);
        setSearchPanelOpen(false);
        setGoToSymbolOpen(false);
        setTimeout(() => {
          if (globalSearchInputRef.current) {
            globalSearchInputRef.current.focus();
          }
        }, 50);
      }
      // Cmd+Shift+H - Global replace in all files
      else if (isMod && event.shiftKey && event.key.toLowerCase() === 'h') {
        event.preventDefault();
        setGlobalSearchOpen(true);
        setGlobalSearchMode('replace');
        setGlobalSearchQuery('');
        setGlobalSearchResults([]);
        setGoToFileOpen(false);
        setCommandPaletteOpen(false);
        setSearchPanelOpen(false);
        setGoToSymbolOpen(false);
        setTimeout(() => {
          if (globalSearchInputRef.current) {
            globalSearchInputRef.current.focus();
          }
        }, 50);
      }
      // Cmd+Shift+O - Go to symbol
      else if (isMod && event.shiftKey && event.key.toLowerCase() === 'o') {
        event.preventDefault();
        setGoToSymbolOpen(true);
        setSymbolQuery('');
        setSymbolHighlight(0);
        setGoToFileOpen(false);
        setCommandPaletteOpen(false);
        setSearchPanelOpen(false);
        setGlobalSearchOpen(false);
        setTimeout(() => {
          if (symbolInputRef.current) {
            symbolInputRef.current.focus();
          }
        }, 50);
      }
      // Cmd+F - Find in file
      else if (isMod && event.key.toLowerCase() === 'f' && !event.shiftKey) {
        event.preventDefault();
        setSearchPanelOpen(true);
        setGoToFileOpen(false);
        setCommandPaletteOpen(false);
        setGlobalSearchOpen(false);
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            searchInputRef.current.select();
          }
        }, 50);
      }
      // Cmd+H - Find and replace in file (only without shift)
      else if (isMod && !event.shiftKey && event.key.toLowerCase() === 'h') {
        event.preventDefault();
        setSearchPanelOpen(true);
        setGoToFileOpen(false);
        setCommandPaletteOpen(false);
        setGlobalSearchOpen(false);
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            searchInputRef.current.select();
          }
        }, 50);
      }
      // F11 - Toggle Zen mode
      else if (event.key === 'F11') {
        event.preventDefault();
        setZenMode(!zenMode);
      }
      // Escape handlers
      else if (event.key === 'Escape' && zenMode) {
        setZenMode(false);
      } else if (event.key === 'Escape' && goToSymbolOpen) {
        setGoToSymbolOpen(false);
      } else if (event.key === 'Escape' && globalSearchOpen) {
        setGlobalSearchOpen(false);
      } else if (event.key === 'Escape' && searchPanelOpen) {
        setSearchPanelOpen(false);
      } else if (event.key === 'Escape' && goToFileOpen) {
        setGoToFileOpen(false);
      } else if (event.key === 'Escape' && commandPaletteOpen) {
        setCommandPaletteOpen(false);
      }
    };
    const options = { capture: true } as const;
    window.addEventListener('keydown', onKeyDown, options);
    return () => window.removeEventListener('keydown', onKeyDown, options);
  }, [
    commandPaletteOpen,
    goToFileOpen,
    searchPanelOpen,
    globalSearchOpen,
    goToSymbolOpen,
    zenMode,
    setCommandHighlight,
    setCommandPaletteOpen,
    setCommandQuery,
    setGoToFileOpen,
    setGoToHighlight,
    setGoToQuery,
    setSearchPanelOpen,
    setGlobalSearchOpen,
    setGlobalSearchMode,
    setGlobalSearchQuery,
    setGlobalSearchResults,
    setGoToSymbolOpen,
    setSymbolQuery,
    setSymbolHighlight,
    setZenMode,
    searchInputRef,
    globalSearchInputRef,
    symbolInputRef,
  ]);

  // Auto-focus GoTo input when opened
  useEffect(() => {
    if (goToFileOpen && goToInputRef.current) {
      goToInputRef.current.focus();
    }
  }, [goToFileOpen, goToInputRef]);

  // Auto-focus Command Palette input when opened
  useEffect(() => {
    if (commandPaletteOpen && commandInputRef.current) {
      commandInputRef.current.focus();
    }
  }, [commandPaletteOpen, commandInputRef]);

  // Auto-focus Symbol input when opened
  useEffect(() => {
    if (goToSymbolOpen && symbolInputRef.current) {
      symbolInputRef.current.focus();
    }
  }, [goToSymbolOpen, symbolInputRef]);
}
